import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';

const chartStyle = { height: 350, width: '100%' };

type BarChartProps = {
    title: string;
    data: number[];
    xAxis: string[];
};

const BarChart: React.FC<BarChartProps> = ({ title, data, xAxis }) => {
    const { t } = useTranslation();
    const formattedData = data.map((value: number) => {
        const isNegative = value < 0;
        return {
            value,
            itemStyle: {
                color: isNegative ? '#ef4444' : '#8b5cf6',
                borderColor: isNegative ? '#dc2626' : '#7c3aed',
                borderWidth: 2,
                borderRadius: [4, 4, 0, 0],
                shadowColor: isNegative ? 'rgba(239, 68, 68, 0.3)' : 'rgba(139, 92, 246, 0.3)',
                shadowBlur: 8,
                shadowOffsetY: 4,
            },
        };
    });

    const option = {
        backgroundColor: 'transparent',
        title: {
            text: title,
            left: 'center',
            top: 20,
            textStyle: { 
                fontSize: 18,
                fontWeight: 'bold',
                color: '#1f2937',
                fontFamily: "Roboto, sans-serif",
            },
        },
        grid: {
            top: 60,
            bottom: 40,
            left: 50,
            right: 30,
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { 
                type: 'shadow',
                shadowStyle: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    shadowBlur: 10,
                }
            },
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            borderRadius: 8,
            padding: [12, 16],
            textStyle: {
                color: '#374151',
                fontSize: 13,
                fontWeight: '500',
                fontFamily: "Roboto, sans-serif",
            },
            formatter: (params: any) => {
                const item = params[0];
                const hour = item.name;
                const result = item.value;
                const isPositive = result >= 0;
                const resultFormatted = `${isPositive ? '+' : '-'}$${Math.abs(result).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const resultColor = isPositive ? '#7c3aed' : '#dc2626';
                const icon = isPositive ? '⏰' : '📉';

                return `
                  <div style="padding: 4px 0;">
                    <div style="font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: 8px;">
                      ${icon} ${hour}:00
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <div style="width: 12px; height: 12px; border-radius: 50%; background: ${item.color};"></div>
                      <span style="color: #6b7280;">${t('Profit')}:</span>
                      <span style="color: ${resultColor}; font-weight: 600; font-size: 14px;">${resultFormatted}</span>
                    </div>
                  </div>
                `;
            },
        },
        xAxis: {
            type: 'category',
            data: xAxis,
            axisLabel: { 
                interval: 0, 
                rotate: 0,
                color: '#6b7280',
                fontSize: 12,
                fontWeight: '500',
            },
            axisLine: { 
                show: true,
                lineStyle: {
                    color: '#e5e7eb',
                    width: 1,
                }
            },
            axisTick: { 
                show: true,
                lineStyle: {
                    color: '#e5e7eb',
                }
            },
            splitLine: {
                show: false,
            }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: '#6b7280',
                fontSize: 12,
                formatter: (value: number) => `$${value.toLocaleString()}`,
            },
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                lineStyle: {
                    color: '#f3f4f6',
                    type: 'dashed',
                }
            }
        },
        series: [
            {
                type: 'bar',
                data: formattedData,
                barWidth: '60%',
                barCategoryGap: '20%',
                barGap: '10%',
                itemStyle: {
                    color: '#8b5cf6',
                    borderColor: '#7c3aed',
                    borderWidth: 2,
                    borderRadius: [4, 4, 0, 0],
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 15,
                        shadowColor: 'rgba(139, 92, 246, 0.4)',
                        borderColor: '#6d28d9',
                        borderWidth: 3,
                    },
                },
                animationDelay: (idx: number) => idx * 100,
                animationDuration: 800,
            },
        ],
        animation: true,
        animationDuration: 1000,
        animationEasing: 'cubicOut',
    };

    return <ReactECharts option={option} style={chartStyle} />;
};


interface DashboardChartsProp {
    hourlyAnalysis: HourlyAnalysis[]
    pdfShow?: boolean
}

interface BaseAnalysis {
    trades: number
    profit: number
    win: number
    losses: number
    win_rate: number
}

interface HourlyAnalysis extends BaseAnalysis {
    hour: string
}


const DashboardCharts = (props: DashboardChartsProp) => {
    const { t } = useTranslation();
    const { hourlyAnalysis, pdfShow } = props

    return (
        <div className={`space-y-6 ${pdfShow ? 'max-w-full' : 'min-w-full'}`}>
            <div className={`portlet ${pdfShow ? 'max-w-full' : 'min-w-full'}`} style={{
                width: pdfShow ? "1075px" : '100%',
            }}>
                <BarChart
                    title={t('ProfitbyHour')}
                    xAxis={hourlyAnalysis.map(item => item.hour)}
                    data={hourlyAnalysis.map(item => item.profit)}
                />
            </div>
        </div>
    );
};

export default DashboardCharts;
